import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import { analyticsRevenueService } from "@/services/analytics-revenue-service";
import type { RevenueCategory } from "@/types/analytics-revenue";
import {
  buildRapidTotalRevenueMetric,
  type ServicesRapidRevenueMetric,
} from "../components/services/servicesRapidMappers";
import {
  getPreviousAnalyticsDateRange,
  toAnalyticsDateParam,
} from "../utils/analyticsDateUtils";
import { useAnalyticsOverviewRange } from "./useAnalyticsOverviewRange";

const PARTNERS_REVENUE_SOURCES: RevenueCategory[] = ["GUESTERIA_PARTNERS"];

export const rapidPartnersRevenueKeys = {
  all: ["analytics-revenue", "rapid-partners"] as const,
  distribution: (hotelId: string, from: string, to: string) =>
    [
      ...rapidPartnersRevenueKeys.all,
      "distribution",
      hotelId,
      from,
      to,
    ] as const,
};

export function useRapidPartnersRevenueKpi(dateRange: DateRange) {
  const rangeParams = useAnalyticsOverviewRange(dateRange);
  const { hotelId, from, to, isReady } = rangeParams;
  const previousRange = useMemo(
    () => getPreviousAnalyticsDateRange(dateRange),
    [dateRange],
  );
  const previousFrom = toAnalyticsDateParam(previousRange?.from);
  const previousTo = toAnalyticsDateParam(previousRange?.to);
  const hasPreviousRange = Boolean(previousFrom && previousTo);

  const current = useQuery({
    queryKey: rapidPartnersRevenueKeys.distribution(hotelId, from, to),
    queryFn: () =>
      analyticsRevenueService.getDistribution({
        hotelId,
        from,
        to,
        sources: PARTNERS_REVENUE_SOURCES,
      }),
    enabled: isReady,
  });

  const previous = useQuery({
    queryKey: rapidPartnersRevenueKeys.distribution(
      hotelId,
      previousFrom ?? "",
      previousTo ?? "",
    ),
    queryFn: () =>
      analyticsRevenueService.getDistribution({
        hotelId,
        from: previousFrom!,
        to: previousTo!,
        sources: PARTNERS_REVENUE_SOURCES,
      }),
    enabled: isReady && hasPreviousRange,
  });

  const metric: ServicesRapidRevenueMetric = useMemo(
    () =>
      buildRapidTotalRevenueMetric({
        current: current.data,
        previous: previous.data,
        includeDelta: hasPreviousRange && !previous.isLoading,
      }),
    [current.data, previous.data, hasPreviousRange, previous.isLoading],
  );

  return {
    metric,
    isLoading: !isReady || current.isLoading,
    isComparisonLoading: hasPreviousRange && previous.isLoading,
    isError: current.isError || previous.isError,
    error: current.error ?? previous.error,
  };
}
